import AddIcon from "@mui/icons-material/Add";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import StairsOutlinedIcon from "@mui/icons-material/StairsOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import {
  Box,
  Button,
  ButtonBase,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { usePendo } from "../../analytics/hooks/use-pendo-watch";
import { BAND, ProgramBadge, tint } from "../../program-landing/components/program-parts";
import { pathText } from "../../program-timeline/move-destinations";
import { levelText, programPath } from "../../program-tree/program-tree";
import { ProgramIcon } from "../../programs/components/program-icon";
import { useCreateProgram } from "../hooks/use-create-program";
import { useUpdateProgram } from "../hooks/use-update-program";

import type { UUID } from "../../../utils/uuid";
import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { ProgramLook } from "../hooks/use-update-program";
import type { FC } from "react";

/** The icons to pick from, by the name that theme_icon keeps. Each one is in public.icon. */
const ICONS: { name: string; label: string; Icon: typeof StairsOutlinedIcon }[] = [
  { name: "StairsOutlined", label: "Stairs", Icon: StairsOutlinedIcon },
  { name: "SchoolOutlined", label: "Learning", Icon: SchoolOutlinedIcon },
  { name: "RocketLaunchOutlined", label: "Launch", Icon: RocketLaunchOutlinedIcon },
  { name: "WorkspacePremiumOutlined", label: "Award", Icon: WorkspacePremiumOutlinedIcon },
  { name: "GroupsOutlined", label: "People", Icon: GroupsOutlinedIcon },
  { name: "HandshakeOutlined", label: "Handshake", Icon: HandshakeOutlinedIcon },
  { name: "LightbulbOutlined", label: "Idea", Icon: LightbulbOutlinedIcon },
  { name: "CelebrationOutlined", label: "Celebration", Icon: CelebrationOutlinedIcon },
  { name: "SavingsOutlined", label: "Savings", Icon: SavingsOutlinedIcon },
  { name: "TrendingUpOutlined", label: "Growth", Icon: TrendingUpOutlinedIcon },
  { name: "FlagOutlined", label: "Goal", Icon: FlagOutlinedIcon },
  { name: "ExploreOutlined", label: "Explore", Icon: ExploreOutlinedIcon },
];

/** The icon that a new folder starts with. It is not one of the suggestions. */
const FOLDER = {
  name: "SubdirectoryArrowRight",
  label: "Folder",
  Icon: SubdirectoryArrowRightIcon,
};

/** The icon and colour that a new top-level program starts with: Stairs and Indigo. */
const NEW_PROGRAM = { icon: "StairsOutlined", colour: "#5048E5" };

const COLOURS: { hex: string; label: string }[] = [
  { hex: "#C208B1", label: "Magenta" },
  { hex: "#5048E5", label: "Indigo" },
  { hex: "#2563EB", label: "Blue" },
  { hex: "#0E7490", label: "Teal" },
  { hex: "#15803D", label: "Green" },
  { hex: "#B45309", label: "Amber" },
  { hex: "#C2410C", label: "Orange" },
  { hex: "#475569", label: "Slate" },
];

const sameColour = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

/** The picked icon, or the program's own icon, as SVG, when it is not one of ICONS. */
const PickedIcon: FC<{ name: string; fallback: string; size: number }> = ({
  name,
  fallback,
  size,
}) => {
  const Icon = [...ICONS, FOLDER].find((option) => option.name === name)?.Icon;
  return Icon ? (
    <Icon sx={{ fontSize: size }} />
  ) : (
    <ProgramIcon data={fallback} sx={{ fontSize: size }} />
  );
};

const headingSx = { fontSize: 14, fontWeight: 600, lineHeight: "20px" } as const;

type FormProps = {
  tree: ProgramTree;
  saving: boolean;
  /** Saves the title, icon and colour, and resolves to whether they were saved. */
  onSubmit: (look: ProgramLook) => Promise<boolean>;
  onClose: () => void;
} & (
  | { create: false; /** The program or folder to change. */ base: ProgramNode }
  | {
      create: true;
      /** The program that the new folder goes in, or null for a new top-level program. */
      base: ProgramNode | null;
    }
);

const ProgramForm: FC<FormProps> = ({ tree, base, create, saving, onSubmit, onClose }) => {
  const start = create ? (base ? FOLDER.name : NEW_PROGRAM.icon) : base.theme_icon;
  const startColour = base?.theme_colour ?? NEW_PROGRAM.colour;
  const fallback = base?.icon_value ?? "";
  const [title, setTitle] = useState(create ? "" : base.title);
  const [touched, setTouched] = useState(false);
  const [icon, setIcon] = useState(start);
  const [colour, setColour] = useState(startColour);
  const named = title.trim();
  // A folder is called a folder, and a top-level program a program.
  const folder = create ? base !== null : base.parent_id !== null;
  const noun = folder ? "folder" : "program";
  const shown = named || `Untitled ${noun}`;
  const where = base ? `${create ? "Inside " : ""}${pathText(tree, base.id)}` : "Top level";
  const level = base ? programPath(tree, base.id).length + (create ? 1 : 0) : 1;
  // A new program or folder has no title to lose, so it asks for one only after a change or a save.
  const missing = !named && (touched || !create);
  const analytics = !create ? "program-edit" : base ? "program-add-folder" : "program-new";
  // The icon and colour that it starts with stay first to pick, if they are not in the lists: a
  // new folder's Folder icon and its program's colour, or the program's own.
  const icons = ICONS.some(({ name }) => name === start)
    ? ICONS
    : [start === FOLDER.name ? FOLDER : { name: start, label: "Current icon" }, ...ICONS];
  const colours = COLOURS.some(({ hex }) => sameColour(hex, startColour))
    ? COLOURS
    : [
        { hex: startColour, label: create && base ? `Colour of ${base.title}` : "Current colour" },
        ...COLOURS,
      ];

  const submit = async (): Promise<void> => {
    setTouched(true);
    if (!named || saving) return;
    if (await onSubmit({ title: named, theme_icon: icon, theme_colour: colour })) onClose();
  };

  return (
    <Stack
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      sx={{ height: "100%" }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          pl: 3,
          pr: 2,
          py: 2.5,
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id="program-edit-title"
            component="h2"
            sx={{ fontSize: 20, fontWeight: 600, lineHeight: "28px" }}
          >
            {create ? `New ${noun}` : `Edit ${noun}`}
          </Typography>
          <Typography noWrap sx={{ fontSize: 13, lineHeight: "20px", color: "text.secondary" }}>
            {where}
          </Typography>
        </Stack>
        <IconButton aria-label="Close" onClick={onClose} data-analytics-id={`${analytics}-close`}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack spacing={3.5} sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        <Stack component="section" aria-label="Preview" spacing={1}>
          <Typography variant="overline" component="h3" sx={{ color: "text.secondary" }}>
            Preview
          </Typography>
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: "8px", overflow: "hidden" }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                p: 2,
                alignItems: "center",
                background: (theme) => (theme.palette.mode === "dark" ? undefined : BAND),
              }}
            >
              <ProgramBadge
                node={{ theme_colour: colour, icon_value: fallback }}
                size={56}
                raised
                icon={<PickedIcon name={icon} fallback={fallback} size={28} />}
              />
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    lineHeight: "16px",
                    textTransform: "uppercase",
                    color: colour,
                  }}
                >
                  {levelText(level)}
                </Typography>
                <Typography noWrap sx={{ fontSize: 18, fontWeight: 700, lineHeight: "24px" }}>
                  {shown}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                px: 2,
                py: 1.25,
                alignItems: "center",
                borderTop: 1,
                borderColor: "divider",
                fontSize: 12,
                color: "text.secondary",
              }}
            >
              <span>Elsewhere in the app</span>
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  height: 24,
                  pl: 1,
                  pr: 1.25,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  borderRadius: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  color: colour,
                  bgcolor: tint(colour),
                }}
              >
                <PickedIcon name={icon} fallback={fallback} size={14} />
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {shown}
                </Box>
              </Box>
            </Stack>
          </Box>
        </Stack>

        <TextField
          label="Title"
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
            setTouched(true);
          }}
          error={missing}
          helperText={missing ? `Give the ${noun} a title.` : undefined}
          fullWidth
        />

        <Stack component="section" aria-labelledby="program-edit-icon" spacing={1.25}>
          <Typography id="program-edit-icon" component="h3" sx={headingSx}>
            Icon
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(44px, 1fr))",
              gap: 0.75,
            }}
          >
            {icons.map(({ name, label }) => {
              const chosen = name === icon;
              return (
                <ButtonBase
                  key={name}
                  onClick={() => setIcon(name)}
                  aria-label={label}
                  aria-pressed={chosen}
                  title={label}
                  sx={{
                    height: 44,
                    borderRadius: "8px",
                    border: chosen ? 2 : 1,
                    borderColor: chosen ? colour : "divider",
                    color: chosen ? colour : "text.secondary",
                    bgcolor: chosen ? tint(colour) : undefined,
                  }}
                  data-analytics-id="program-edit-icon"
                >
                  <PickedIcon name={name} fallback={fallback} size={22} />
                </ButtonBase>
              );
            })}
          </Box>
        </Stack>

        <Stack component="section" aria-labelledby="program-edit-colour" spacing={1.25}>
          <Stack spacing={0.25}>
            <Typography id="program-edit-colour" component="h3" sx={headingSx}>
              Colour
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Used for the icon and to mark this program’s communications across the app.
            </Typography>
          </Stack>
          <Stack direction="row" useFlexGap sx={{ flexWrap: "wrap", gap: 0.5 }}>
            {colours.map(({ hex, label }) => {
              const chosen = sameColour(hex, colour);
              return (
                <ButtonBase
                  key={hex}
                  onClick={() => setColour(hex)}
                  aria-label={label}
                  aria-pressed={chosen}
                  title={label}
                  sx={{ width: 44, height: 44, borderRadius: "50%" }}
                  data-analytics-id="program-edit-colour"
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "common.white",
                      bgcolor: hex,
                      boxShadow: (theme) =>
                        chosen
                          ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${hex}`
                          : "none",
                    }}
                  >
                    {chosen ? <CheckIcon sx={{ fontSize: 18 }} /> : null}
                  </Box>
                </ButtonBase>
              );
            })}
          </Stack>
        </Stack>
      </Stack>

      {create ? (
        <Stack
          direction="row"
          spacing={1.25}
          sx={{
            mx: 3,
            mb: 2,
            p: 1.5,
            border: 1,
            borderColor: "divider",
            borderRadius: "8px",
            bgcolor: "action.hover",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 20, mt: 0.25, color: "text.secondary" }} />
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: "20px" }}>
              {base
                ? `Access will follow ${base.title}`
                : "Only admins will have access when it’s created."}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {base ? `The people with access to ${base.title} will have access to it. ` : null}
              You can change this from Access afterwards.
            </Typography>
          </Stack>
        </Stack>
      ) : null}

      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 3, py: 2, justifyContent: "flex-end", borderTop: 1, borderColor: "divider" }}
      >
        <Button onClick={onClose} data-analytics-id={`${analytics}-cancel`}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!named || saving}
          data-analytics-id={`${analytics}-save`}
        >
          {create ? `Create ${noun}` : "Save changes"}
        </Button>
      </Stack>
    </Stack>
  );
};

/**
 * A program's title, icon and colour, or a new program's or folder's, in a drawer at the right, with
 * a preview. It does not close while it saves.
 */
const ProgramDrawer: FC<FormProps & { open: boolean }> = ({ open, onClose, ...props }) => {
  const close = (): void => {
    if (!props.saving) onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-labelledby": "program-edit-title",
          sx: { width: 480, maxWidth: "100%" },
        },
      }}
    >
      <ProgramForm {...props} onClose={close} />
    </Drawer>
  );
};

/**
 * Opens the drawer that edits a program or a folder. An admin can change either; a contributor can
 * change a folder that they can edit.
 */
export const EditProgramButton: FC<{
  tree: ProgramTree;
  program: ProgramNode;
  /** After the program is saved, so that the programs can load again. */
  onSaved: () => void;
}> = ({ tree, program, onSaved }) => {
  const [open, setOpen] = useState(false);
  const { save, saving } = useUpdateProgram();
  const pendo = usePendo();

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<EditOutlinedIcon />}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        data-analytics-id="program-edit-open"
      >
        {program.parent_id ? "Edit folder" : "Edit program"}
      </Button>
      <ProgramDrawer
        open={open}
        onClose={() => setOpen(false)}
        tree={tree}
        base={program}
        create={false}
        saving={saving}
        onSubmit={async (look) => {
          const saved = (await save(program.id, look)) === true;
          if (saved) {
            pendo?.track("program-edited", {
              programId: program.id,
              folder: program.parent_id !== null,
            });
            onSaved();
          }
          return saved;
        }}
      />
    </>
  );
};

/** The drawer that makes a folder in `parent`, or a top-level program when there is no parent. */
const CreateDrawer: FC<{
  tree: ProgramTree;
  parent: ProgramNode | null;
  open: boolean;
  onClose: () => void;
  /** After it is made, with its id. */
  onCreated: (id: UUID) => void;
}> = ({ tree, parent, open, onClose, onCreated }) => {
  const { create, creating } = useCreateProgram();
  const pendo = usePendo();

  return (
    <ProgramDrawer
      open={open}
      onClose={onClose}
      tree={tree}
      base={parent}
      create
      saving={creating}
      onSubmit={async (look) => {
        const id = await create(tree, parent, look);
        if (id) {
          if (parent)
            pendo?.track("program-folder-created", { programId: id, parentId: parent.id });
          else pendo?.track("program-created", { programId: id });
          onCreated(id);
        }
        return id !== undefined;
      }}
    />
  );
};

/**
 * Opens the New folder drawer, to make a folder in a program. An admin, or a contributor who can
 * edit the program, can make one.
 */
export const AddFolderButton: FC<{
  tree: ProgramTree;
  parent: ProgramNode;
  /** After the folder is made, with its id. */
  onCreated: (id: UUID) => void;
}> = ({ tree, parent, onCreated }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`Add folder to ${parent.title}`}
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        data-analytics-id="program-add-folder-open"
      >
        Add folder
      </Button>
      <CreateDrawer
        tree={tree}
        parent={parent}
        open={open}
        onClose={() => setOpen(false)}
        onCreated={onCreated}
      />
    </>
  );
};

/**
 * Opens the New program drawer, to make a top-level program. Only an admin can make one, and only
 * admins have access to it until someone changes its access.
 */
export const NewProgramButton: FC<{
  tree: ProgramTree;
  /** After the program is made, with its id. */
  onCreated: (id: UUID) => void;
}> = ({ tree, onCreated }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        data-analytics-id="programs-landing-new"
      >
        New program
      </Button>
      <CreateDrawer
        tree={tree}
        parent={null}
        open={open}
        onClose={() => setOpen(false)}
        onCreated={onCreated}
      />
    </>
  );
};
